/// <amd-module name="Checks/ChecksOverview/ChecksOverview" />
import * as React from "react";

import { ChecksActionsCreator } from "Checks/ChecksOverview/ChecksActionsCreator";
import { ChecksDetailsTable } from "Checks/ChecksOverview/ChecksDetailsTable";
import { ChecksPanel } from "Checks/ChecksOverview/ChecksPanel";
import { ChecksSource } from "Checks/ChecksOverview/ChecksSource";
import { ChecksStore } from "Checks/ChecksOverview/ChecksStore";
import * as Resources from "Checks/ChecksOverview/Resources";
import { FeatureNames, markTimeToInteractive, publishTelemetry } from "Checks/ChecksOverview/Telemetry";
import { CheckAction } from "Checks/Client/Contracts/Contracts";
import * as PipelineChecksContracts from "Checks/Client/Contracts/PipelinesChecks";

import { ObservableValue } from "VSS/Core/Observable";
import { announce } from "VSS/Core/Util/Accessibility";
import { equals, format } from "VSS/Core/Util/String";
import { IVssPageContext } from "VSS/Platform/Context";
import { isFeatureFlagEnabled } from "VSS/Platform/Feature";
import { IVssComponentContext, IVssComponentProperties, VssComponent, css } from "VSS/Platform/Layout";
import { getAssetLocation } from "VSS/Platform/Location";

import { Button } from "VSSUI/Button";
import { TitleSize } from "VSSUI/Header";
import { Icon } from "VSSUI/Icon";
import { Image } from "VSSUI/Image";
import { IListItemDetails, IListRow, List, ListItem } from "VSSUI/List";
import { MessageCard, MessageCardSeverity } from "VSSUI/MessageCard";
import { Observer } from "VSSUI/Observer";
import { Panel } from "VSSUI/Panel";
import { RadioButton, RadioButtonGroup } from "VSSUI/RadioButton";
import { TextField, TextFieldStyle, TextFieldWidth } from "VSSUI/TextField";
import { KeyCode } from "VSSUI/Util";
import { ArrayItemProvider } from "VSSUI/Utilities/Provider";
import { Link } from "VSSUI/Link";

export interface IState {
    showNoChecksExperience: boolean;
    checkConfigurationDataList: PipelineChecksContracts.CheckConfigurationData[];
    errorMessage?: string;
    defaultCheckSettings?: { [key: string]: string; };
}

export interface IProps extends IVssComponentProperties {
    resourceType: string;
    resourceId: string;
    resourceName: string;
    hasManagePermission: boolean;
    dataProviderRequestParameters?: { [key: string]: any };
}

export interface ICheckDefinitionRowProps extends IVssComponentProperties {
    checkDefinitionRefId: string;
    checkIcon: PipelineChecksContracts.CheckIcon;
    checkContributionId: string;
    checkHeader: string;
    checkDescription: string;
    checkEvaluationOrder?: number;
    onCheckClicked?: (checkContributionId: string) => void;
}

export class ChecksOverview extends VssComponent<IProps, IState> {

    public constructor(props: IProps, context: IVssComponentContext) {
        super(props, context);
        this._pageContext = context.pageContext;
        this._source = ChecksSource.getInstance(this._pageContext);

        let dataProviderRequestParameters = this.props.dataProviderRequestParameters || {};
        const checkData = this._source.getData<PipelineChecksContracts.CheckData>("ms.vss-pipelinechecks.checks-data-provider", dataProviderRequestParameters) as PipelineChecksContracts.CheckData;
        this._isDisableChecksFeatureEnabled = isFeatureFlagEnabled(context.pageContext, "Pipelines.Checks.EnableDisabledChecksFeature", false);
        const data: PipelineChecksContracts.CheckConfigurationData[] = checkData ? checkData.checkConfigurationDataList : [];

        this._isEnableOrderingChecksEnabled = isFeatureFlagEnabled(context.pageContext, "Pipelines.Checks.EnableOrderingChecks", false);

        this._actionsCreator = new ChecksActionsCreator(context.pageContext);
        this._store = new ChecksStore(data, checkData.checkDefinitions);
        this._store.addChangedListener(this._handleStoreChange);
        let storeState: PipelineChecksContracts.CheckConfigurationData[] = this._store.getState();

        this._checkDefinitions = this._store._getCheckDefinitions();

        this._isCheckConfigPanelOpen = new ObservableValue<boolean>(false);
        this._checksListPanelOpen = new ObservableValue<boolean>(false);
        this._selectedCheckOption = new ObservableValue<string>("");
        this._searchTextObservable = new ObservableValue<string>("");
        this.state = {
            showNoChecksExperience: !this._areChecksConfigured(storeState),
            checkConfigurationDataList: storeState,
            defaultCheckSettings: checkData.defaultCheckSettings
        };
    }

    public componentDidMount(): void {
        super.componentDidMount();

        let telemetryProperties = {
            ProjectAndResourceIdentifier: this._getProjectAndResourceIdentifier(),
            IsDay0CardDisplayed: this.state.showNoChecksExperience ? "true" : "false"
        };
        markTimeToInteractive(this._pageContext);
        publishTelemetry(this._pageContext, FeatureNames.ChecksPage, telemetryProperties);
    }

    public componentWillUnmount(): void {
        super.componentWillUnmount();
        this._store.removeChangedListener(this._handleStoreChange);
        this._store.dispose();
    }

    public render() {
        const resource: PipelineChecksContracts.Resource = {
            type: this.props.resourceType,
            id: this.props.resourceId,
            name: this.props.resourceName
        };

        this._availableCheckDefinitionsList = this._getAvailableCheckDefinitions(this._checkDefinitions);
        let showAddCheckButton = this._availableCheckDefinitionsList.length > 0;

        if (this.state.showNoChecksExperience) {
            const imagePath = getAssetLocation(this.context.pageContext, "ms.vss-pipelinechecks/web/Content/no-checks.png");
            return (
                <div>
                    {
                        showAddCheckButton && this._getAddChecksButton()
                    }
                    {!this.props.hasManagePermission && this._getNoPermissionMessageSection()}
                    <div className={css("no-checks-container", this.props.className)}>
                        <div>
                            {
                                imagePath &&
                                <img className="icon checks-overview-image" src={imagePath} alt="no-checks-image" />
                            }
                        </div>
                        <div className="checks-headers">
                            <span className="checks-header title-l">{Resources.ChecksHeader}</span>
                            <span className="checks-description body-m">{Resources.ChecksDescription}</span>
                            <span className="checks-description body-m">{Resources.DetailedDescription}</span>
                            <List
                                className="checks-list"
                                itemProvider={new ArrayItemProvider<ICheckDefinitionRowProps>(this._availableCheckDefinitionsList.slice(0, 3))}
                                renderRow={this._renderChecksRow}
                                {...(this.props.hasManagePermission ? { onActivate: this._listItemClick, singleClickActivation: true } : {})}
                            />
                            {
                                this._availableCheckDefinitionsList.length > 3 &&
                                <Button onClick={() => { this._checksListPanelOpen.value = true; }}>{Resources.SeeAllChecksLabel}</Button>
                            }
                        </div>
                        <Observer checksListPanelOpen={this._checksListPanelOpen} configChecksPanelOpen={this._isCheckConfigPanelOpen}>
                            {(observerProps: { checksListPanelOpen: boolean, configChecksPanelOpen: boolean }) => {
                                return this._getObservedComponents(observerProps.checksListPanelOpen, observerProps.configChecksPanelOpen, resource);
                            }}
                        </Observer>
                    </div>
                </div>
            );
        }
        else {
            let errorSection: JSX.Element = this._getErrorMessageSection();

            return (
                <div>
                    {this.state.errorMessage && errorSection}
                    {
                        !this._isEnableOrderingChecksEnabled && showAddCheckButton && this._getAddChecksButton()
                    }
                    <div className="checks-overview-container">
                        {!this.props.hasManagePermission && this._getNoPermissionMessageSection()}
                        {this.state.checkConfigurationDataList &&
                            <ChecksDetailsTable
                                checkConfigurationData={this.state.checkConfigurationDataList}
                                checkDefinitions={this._getFilteredCheckDefinitions()}
                                hasManagePermission={this.props.hasManagePermission}
                                onUpdateCheck={this._onUpdateCheck}
                                onDeleteCheck={this._onDeleteCheck}
                                defaultCheckSettings={this.state.defaultCheckSettings}
                                showAddCheckButton={this._isEnableOrderingChecksEnabled && showAddCheckButton}
                                onAddCheckButtonClicked={this._onAddCheckButtonClicked}
                            />
                        }
                        <Observer checksListPanelOpen={this._checksListPanelOpen} configChecksPanelOpen={this._isCheckConfigPanelOpen}>
                            {(observerProps: { checksListPanelOpen: boolean, configChecksPanelOpen: boolean }) => {
                                return this._getObservedComponents(observerProps.checksListPanelOpen, observerProps.configChecksPanelOpen, resource);
                            }}
                        </Observer>
                    </div>
                </div>
            );
        }
    }

    private _getAddChecksButton = (): JSX.Element => {
        return (
            <div className="checks-create-button-container">
                <Button className="checks-create-button" disabled={!this.props.hasManagePermission}
                    ariaLabel={Resources.AddCheck} iconProps={{ iconName: "Add", tooltipProps: { text: Resources.AddCheck } }} onClick={this._onAddCheckButtonClicked} />
            </div>
        );
    }

    private _getChecksListPanel = (): JSX.Element => {
        this._checksListData = this._isEnableOrderingChecksEnabled ? this._availableCheckDefinitionsList.sort((a, b) => a.checkEvaluationOrder! < b.checkEvaluationOrder! ? -1 : 1) : this._availableCheckDefinitionsList;

        if (this._checksListData.length > 0) {
            this._selectedCheckOption.value = this._checksListData[0].checkDefinitionRefId;
        }

        return (
            <Observer selectedCheckOption={this._selectedCheckOption}>
                {(observerProps: { selectedCheckOption: string }) => {
                    return (
                        <Panel
                            className={"checks-list-panel"}
                            onDismiss={this._onClosePanel}
                            titleProps={{ text: Resources.ChecksPanelHeader, size: TitleSize.Large, className: css(this._isEnableOrderingChecksEnabled && "margin-bottom-16") }}
                            descriptionItem={ this._isEnableOrderingChecksEnabled &&
                                (
                                <div className="bolt-header-description body-m secondary-text">
                                    {Resources.ChecksPanelDescription}
                                    {" "}
                                    <Link href={Resources.CheckEvaluationOrderLearnMoreLink}>{Resources.CheckPanelDescriptionLearnMore}</Link>
                                </div>
                                )}
                            footerButtonProps={[
                                { text: Resources.Next, primary: true, disabled: observerProps.selectedCheckOption.length === 0, onClick: this._onCheckTypeSelected }
                            ]} >
                            <div className="checks-list-panel-content">
                                <div className="checks-search-container">
                                    <TextField
                                        value={this._searchTextObservable}
                                        width={TextFieldWidth.auto}
                                        placeholder={Resources.SearchChecksLabel}
                                        ariaLabel={Resources.SearchChecksLabel}
                                        prefixIconProps={{ iconName: "Search" }}
                                        onChange={(event, value) => this._onSearchTextChanged(value)}
                                        style={TextFieldStyle.inline} />
                                </div>
                                <div className="checks-list">
                                    <RadioButtonGroup
                                        onSelect={selectedId => (this._selectedCheckOption.value = selectedId)}
                                        selectedButtonId={this._selectedCheckOption}>
                                        {
                                            this._checksListData.map((check, i) => {
                                                let isTheFirstOfItsEvaluationOrder;
                                                let shouldHaveBottomPadding; 

                                                if (this._isEnableOrderingChecksEnabled) {
                                                    isTheFirstOfItsEvaluationOrder = i - 1 >= 0 && this._checksListData[i].checkEvaluationOrder !== this._checksListData[i - 1].checkEvaluationOrder;
                                                    shouldHaveBottomPadding = i + 1 < this._checksListData.length && this._checksListData[i].checkEvaluationOrder !== this._checksListData[i + 1].checkEvaluationOrder;
                                                }

                                                return (
                                                    <div className={css("flex-row", "flex-center", isTheFirstOfItsEvaluationOrder && "separator-line-top padding-top-8", shouldHaveBottomPadding && "padding-bottom-8")} key={"check-" + check.checkHeader}>
                                                        <RadioButton id={check.checkDefinitionRefId}>
                                                            <CheckDefinitionRow
                                                                checkDefinitionRefId={check.checkDefinitionRefId}
                                                                checkContributionId={check.checkContributionId}
                                                                checkIcon={check.checkIcon}
                                                                checkHeader={check.checkHeader}
                                                                checkDescription={check.checkDescription} />
                                                        </RadioButton>
                                                    </div>
                                                );
                                            })
                                        }
                                    </RadioButtonGroup>
                                </div>
                            </div>
                        </Panel>
                    );
                }}
            </Observer>
        );
    }

    private _getObservedComponents = (showAddCheckPanel: boolean, showCheckConfigPanel: boolean, resource: PipelineChecksContracts.Resource): JSX.Element => {
        //Seperate the contributionId from the local check Id
        let componentCheckDefinition = this._checkDefinitionRefIdToDefinitionMap[this._selectedCheckOption.value];
        let componentContributionProps = componentCheckDefinition ? componentCheckDefinition.checkDefinition : {};
        let componentContributionDependencies = componentCheckDefinition ? componentCheckDefinition.uiContributionDependencies : [];
        return (
            <div>
                {showAddCheckPanel && this._getChecksListPanel()}
                {showCheckConfigPanel &&
                    <ChecksPanel
                        contributionType={componentCheckDefinition.uiContributionType || ""}
                        onUpdateCheck={this._onAddCheck}
                        onClosePanel={this._onClosePanel}
                        resource={resource}
                        checkDefinition={componentContributionProps}
                        checkAction={CheckAction.Create}
                        checkName={componentCheckDefinition ? componentCheckDefinition.name : ""}
                        checkDefinitionRefId={componentCheckDefinition ? componentCheckDefinition.definitionRefId : ""}
                        isDisableChecksFeatureEnabled={this._isDisableChecksFeatureEnabled}
                        componentDependencies={componentContributionDependencies}
                        defaultCheckSettings={this.state.defaultCheckSettings} />}
            </div>
        );
    }

    private _renderChecksRow = (index: number, check: ICheckDefinitionRowProps, checkList: IListItemDetails<ICheckDefinitionRowProps>): JSX.Element => {
        let checksRowClass = "checks-row" + (this.props.hasManagePermission ? " checks-row-enabled" : "");
        return (
            <ListItem key={"list-item-" + index} index={index} details={checkList}>
                <div className={checksRowClass}>
                    <CheckDefinitionRow
                        checkDefinitionRefId={check.checkDefinitionRefId}
                        checkIcon={check.checkIcon}
                        checkContributionId={check.checkContributionId}
                        checkHeader={check.checkHeader}
                        checkDescription={check.checkDescription} />
                </div>
            </ListItem>
        );
    }

    private _getAvailableCheckDefinitions = (checkDefinitions: PipelineChecksContracts.CheckDefinitionData[]): ICheckDefinitionRowProps[] => {
        let checksList: ICheckDefinitionRowProps[] = [];

        checkDefinitions.map((checkDefinitionData) => {
            if (this._shouldAddCheck(checkDefinitionData)) {
                checksList.push({
                    checkEvaluationOrder: this._isEnableOrderingChecksEnabled ? checkDefinitionData.checkEvaluationOrder : undefined,
                    checkDefinitionRefId: checkDefinitionData.definitionRefId,
                    checkIcon: {
                        name: checkDefinitionData.icon.name,
                        url: checkDefinitionData.icon.url,
                        assetLocation: checkDefinitionData.icon.assetLocation
                    },
                    checkContributionId: checkDefinitionData.uiContributionType,
                    checkHeader: checkDefinitionData.name,
                    checkDescription: checkDefinitionData.description,
                    onCheckClicked: this._onCheckRowClicked
                });

                if (!this._checkDefinitionRefIdToDefinitionMap[checkDefinitionData.definitionRefId]) {
                    this._checkDefinitionRefIdToDefinitionMap[checkDefinitionData.definitionRefId] = checkDefinitionData;
                }
            }
        });

        return checksList;
    }

    private _onSeeAllChecksPressed = (event: React.KeyboardEvent<HTMLDivElement>) => {
        if (event.which === KeyCode.enter) {
            this._checksListPanelOpen.value = true;
            event.preventDefault();
        }
    }

    private _onSearchTextChanged(value: string): void {
        this._searchTextObservable.value = value;
        this._actionsCreator.filterChecks(value);
    }

    private _getFilteredCheckDefinitions(): PipelineChecksContracts.CheckDefinitionData[] {
        let configuredChecksDefinitionRefList = this.state.checkConfigurationDataList.map(c => c.definitionRefId);

        let filteredCheckDefinitions = this._checkDefinitions.filter(definition => configuredChecksDefinitionRefList.indexOf(definition.definitionRefId) !== -1);

        return filteredCheckDefinitions;
    }

    private _shouldAddCheck(checkDefinition: PipelineChecksContracts.CheckDefinitionData): boolean {
        if (checkDefinition.allowMultipleConfigurations) {
            return true;
        }
        let isCheckConfigured: boolean = false;
        let checks = this.state.checkConfigurationDataList;
        for (let check of checks) {
            if (equals(check.definitionRefId, checkDefinition.definitionRefId, true)) {
                isCheckConfigured = true;
                break;
            }
        }

        return !isCheckConfigured;
    }

    private _listItemClick = (event: React.SyntheticEvent<HTMLElement>, listRow: IListRow<ICheckDefinitionRowProps>): void => {
        if (listRow.data.onCheckClicked) {
            listRow.data.onCheckClicked(listRow.data.checkDefinitionRefId);
        }
    }

    private _onCheckRowClicked = (checkDefinitionRefId: string): any => {
        this._selectedCheckOption.value = checkDefinitionRefId;
        this._isCheckConfigPanelOpen.value = true;
    }

    private _onAddCheckButtonClicked = (): void => {
        this._checksListPanelOpen.value = true;
    }

    private _onCheckTypeSelected = (): void => {
        this._checksListPanelOpen.value = false;
        this._searchTextObservable.value = "";
        this._isCheckConfigPanelOpen.value = true;
    }

    private _getNoPermissionMessageSection(): JSX.Element {
        return (<div className="margin-bottom">
            <MessageCard
                className="checks-no-permission-message-section"
                severity={MessageCardSeverity.Warning}>
                {Resources.NoPermissionMessage}
            </MessageCard>
        </div>);
    }

    private _getErrorMessageSection(): JSX.Element {
        return (<div className="margin-bottom">
            <MessageCard
                className="checks-error-message-section"
                onDismiss={this._onErrorMessageDismiss}
                severity={MessageCardSeverity.Error}>
                {this.state.errorMessage}
            </MessageCard>
        </div>);
    }

    private _onErrorMessageDismiss = (): void => {
        this.setState({ errorMessage: "" });
    }

    private _onClosePanel = (): void => {
        this._checksListPanelOpen.value = false;
        this._searchTextObservable.value = "";
        this._isCheckConfigPanelOpen.value = false;
    }

    private _onAddCheck = (check: PipelineChecksContracts.CheckConfiguration, definitionRefId: string, checkType: string): Promise<void> => {
        return this._actionsCreator.addCheck(check, definitionRefId).then(() => {
            announce(format(Resources.CheckAddedAnnounceMessage, checkType));
            return Promise.resolve();
        }, (error) => {
            announce(format(Resources.CheckNotAddedAnnounceMessage, checkType, error.message));
            return Promise.reject(error);
        });
    }

    private _onDeleteCheck = (checkAssignmentId: number, checkType: string): void => {
        this._actionsCreator.deleteCheck(checkAssignmentId).then(() => {
            const telemetryProperties = {
                ProjectAndResourceIdentifier: this._getProjectAndResourceIdentifier(),
                CheckType: checkType
            };
            publishTelemetry(this._pageContext, FeatureNames.DeleteCheck, telemetryProperties);
            this.setState({ errorMessage: "" });
            announce(format(Resources.CheckDeletedAnnounceMessage, checkType));
        }, (error) => {
            this.setState({ errorMessage: error && error.message });
            announce(format(Resources.CheckNotDeletedAnnounceMessage, checkType, error.message));
        });
    }

    private _onUpdateCheck = (check: PipelineChecksContracts.CheckConfiguration, definitionRefId: string, checkType: string, showError: boolean): Promise<void> => {
        return this._actionsCreator.updateCheck(check, definitionRefId).then(() => {
            announce(format(Resources.CheckUpdatedAnnounceMessage, checkType));
            if (showError) {
                this.setState({ errorMessage: "" });
            }
            return Promise.resolve();
        }, (error) => {
            announce(format(Resources.CheckNotUpdatedAnnounceMessage, checkType, error.message));
            if (showError) {
                this.setState({ errorMessage: error && error.message});
            }
            return Promise.reject(error);
        });
    }

    private _areChecksConfigured(data: PipelineChecksContracts.CheckConfigurationData[]) {
        if (!data || data.length === 0) {
            return false;
        }
        return true;
    }

    private _handleStoreChange = (): void => {
        if (this._checksListPanelOpen.value) {
            this._checksListData = this._getAvailableCheckDefinitions(this._store._getFilteredCheckDefinitions());
            this._selectedCheckOption.value = this._checksListData.length > 0 ? this._checksListData[0].checkDefinitionRefId : "";

            // Make the narrator announce that these many check types are visible.
            announce(this._getFilteredChecksNarratorMessage());
        }
        else {
            let storeState = this._store.getState();
            this.setState({ showNoChecksExperience: !this._areChecksConfigured(storeState), checkConfigurationDataList: storeState });
        }
    }

    private _getFilteredChecksNarratorMessage(): string {
        const checksListDataLength = this._checksListData.length;
        const searchText = this._searchTextObservable.value;
        if (checksListDataLength > 0) {
            return checksListDataLength === 1 ? format(Resources.SingleFilteredCheckAvailableMessage, searchText) : format(Resources.FilteredChecksAvailableMessage, checksListDataLength, searchText);
        }
        return format(Resources.NoChecksFilteredMessage, searchText);
    }

    private _getProjectAndResourceIdentifier(): string {
        return format("{0}:{1}:{2}", this._source._project, this.props.resourceType, this.props.resourceId);
    }

    private _actionsCreator: ChecksActionsCreator;
    private _store: ChecksStore;
    private _isCheckConfigPanelOpen: ObservableValue<boolean>;
    private _checksListPanelOpen: ObservableValue<boolean>;
    private _searchTextObservable: ObservableValue<string>;
    private _selectedCheckOption: ObservableValue<string>;
    private _pageContext: IVssPageContext;
    private _source: ChecksSource;
    private _checkDefinitions: PipelineChecksContracts.CheckDefinitionData[];
    private _checkDefinitionRefIdToDefinitionMap: { [key: string]: PipelineChecksContracts.CheckDefinitionData } = {};
    private _availableCheckDefinitionsList: ICheckDefinitionRowProps[];
    private _checksListData: ICheckDefinitionRowProps[];
    private _isDisableChecksFeatureEnabled: boolean;
    private _isEnableOrderingChecksEnabled: boolean;
}


class CheckDefinitionRow extends VssComponent<ICheckDefinitionRowProps, {}> {

    public constructor(props: ICheckDefinitionRowProps, context: IVssComponentContext) {
        super(props, context);
    }

    public render(): JSX.Element {
        if (this.props.checkIcon.assetLocation) {
            this.props.checkIcon.url = getAssetLocation(this.context.pageContext, this.props.checkIcon.assetLocation);
        }
        return (
            <div className="flex-row flex-center">
                {this.props.checkIcon.url ?
                    <Image src={this.props.checkIcon.url} alt={this.props.checkHeader + "-icon"} className="checks-icon-image" />
                    : <Icon iconName={this.props.checkIcon.name} className="checks-icon" />
                }
                <div className="checks-list-item">
                    <div className="checks-list-header primary-text body-m">
                        {this.props.checkHeader}
                    </div>
                    <div className="checks-list-description secondary-text body-s">
                        {this.props.checkDescription}
                    </div>
                </div>
            </div>
        );
    }
}

VssComponent.register("checks-overview-component", ChecksOverview);